/** Color themes, ported from Theme.java. Colors are CSS rgba() strings. */

export interface Theme {
  name: string;
  panelBg: string;
  lightSq: string;
  darkSq: string;
  selSq: string; // semi-opaque overlay
  hlDest: string;
  lastMv: string;
  whitePc: string;
  blackPc: string;
  whiteEdge: string;
  blackEdge: string;
  label: string;
  outputBg: string;
  outputFg: string;
}

const rgb = (r: number, g: number, b: number) => `rgb(${r},${g},${b})`;
const rgba = (r: number, g: number, b: number, a: number) =>
  `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`;

export const CLASSIC: Theme = {
  name: 'Classic',
  panelBg: rgb(0x30, 0x30, 0x30),
  lightSq: rgb(0xee, 0xd9, 0xb5),
  darkSq: rgb(0xb5, 0x88, 0x63),
  selSq: rgba(0x6b, 0xae, 0xd6, 0xb0),
  hlDest: rgba(0x66, 0xbb, 0x6a, 0xa0),
  lastMv: rgba(0xff, 0xeb, 0x3b, 0x80),
  whitePc: rgb(0xf5, 0xf5, 0xf5),
  blackPc: rgb(0x2e, 0x2e, 0x2e),
  whiteEdge: rgb(0x20, 0x20, 0x20),
  blackEdge: rgb(0x20, 0x20, 0x20),
  label: rgb(0xc8, 0xc8, 0xc8),
  outputBg: rgb(0x1e, 0x1e, 0x1e),
  outputFg: rgb(0xe0, 0xe0, 0xe0),
};

export const SLATE: Theme = {
  name: 'Slate',
  panelBg: rgb(0x1f, 0x29, 0x37),
  lightSq: rgb(0xcb, 0xd5, 0xe1),
  darkSq: rgb(0x47, 0x55, 0x69),
  selSq: rgba(0x60, 0xa5, 0xfa, 0xc0),
  hlDest: rgba(0x4a, 0xde, 0x80, 0xb0),
  lastMv: rgba(0xfc, 0xd3, 0x4d, 0x90),
  whitePc: rgb(0xf1, 0xf5, 0xf9),
  blackPc: rgb(0x1e, 0x29, 0x3b),
  whiteEdge: rgb(0x0b, 0x12, 0x1f),
  blackEdge: rgb(0x0b, 0x12, 0x1f),
  label: rgb(0xcb, 0xd5, 0xe1),
  outputBg: rgb(0x0f, 0x17, 0x24),
  outputFg: rgb(0xcb, 0xd5, 0xe1),
};

export const HIGH_CONTRAST: Theme = {
  name: 'High Contrast',
  panelBg: rgb(0, 0, 0),
  lightSq: rgb(0xff, 0xff, 0xff),
  darkSq: rgb(0x40, 0x40, 0x40),
  selSq: rgba(0x00, 0x80, 0xff, 0xe0),
  hlDest: rgba(0x00, 0xff, 0x00, 0xc0),
  lastMv: rgba(0xff, 0xff, 0x00, 0xc0),
  whitePc: rgb(0xff, 0xff, 0xff),
  blackPc: rgb(0x00, 0x00, 0x00),
  whiteEdge: rgb(0x00, 0x00, 0x00),
  blackEdge: rgb(0xff, 0xff, 0xff),
  label: rgb(0xff, 0xff, 0xff),
  outputBg: rgb(0x00, 0x00, 0x00),
  outputFg: rgb(0xff, 0xff, 0xff),
};

export const SEPIA: Theme = {
  name: 'Sepia',
  panelBg: rgb(0x4a, 0x37, 0x28),
  lightSq: rgb(0xf1, 0xe0, 0xc0),
  darkSq: rgb(0x8c, 0x68, 0x4b),
  selSq: rgba(0xd9, 0x9f, 0x4a, 0xc0),
  hlDest: rgba(0xa1, 0xc9, 0x65, 0xb0),
  lastMv: rgba(0xe5, 0xc4, 0x4c, 0x90),
  whitePc: rgb(0xfb, 0xf1, 0xdc),
  blackPc: rgb(0x4a, 0x32, 0x1f),
  whiteEdge: rgb(0x2c, 0x1a, 0x10),
  blackEdge: rgb(0x2c, 0x1a, 0x10),
  label: rgb(0xe8, 0xd4, 0xb0),
  outputBg: rgb(0x2c, 0x20, 0x18),
  outputFg: rgb(0xe8, 0xd4, 0xb0),
};

export const ALL: Theme[] = [CLASSIC, SLATE, HIGH_CONTRAST, SEPIA];
